import * as htmlToImage from "html-to-image";
import { CalendarDays, Copy, Download, Link, MapPin } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import Spinner from "../../components/Spinner";
import { Button } from "../../components/ui/button";

const [HEIGHT, WIDTH] = [1350, 1080]; // Default dimensions for the image

const TEMPLATES = [
	{ id: "classic", name: "Classic", component: TemplateClassic },
	{ id: "editorial", name: "Editorial", component: TemplateEditorial },
];

function GenerateSingleJobImage({
	companyName,
	jobTitle,
	jobDescription,
	qualification,
	jobLink,
	deadline,
	jobLocation,
}) {
	const imageRef = React.useRef(null);
	const [isDownloading, setIsDownloading] = React.useState(false);
	const [isCopying, setIsCopying] = React.useState(false);
	const [selectedTemplate, setSelectedTemplate] = React.useState(TEMPLATES[0].id);

	const downloadImage = async () => {
		if (imageRef.current && !isDownloading) {
			setIsDownloading(true);
			try {
				const dataUrl = await htmlToImage.toPng(imageRef.current, {
					quality: 3,
					pixelRatio: 10,
				});
				const link = document.createElement("a");
				link.download = `${companyName}-job-openings.png`;
				link.href = dataUrl;
				link.click();
			} catch (error) {
				toast.error("Failed to download image" + error);
			} finally {
				setIsDownloading(false);
			}
		}
	};

	const copyToClipboard = async () => {
		if (imageRef.current && !isCopying) {
			setIsCopying(true);
			try {
				const blob = await htmlToImage.toBlob(imageRef.current, {
					quality: 3,
					pixelRatio: 10,
				});
				await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
				toast.success("Image copied to clipboard!");
			} catch (error) {
				toast.error("Failed to copy image to clipboard");
			} finally {
				setIsCopying(false);
			}
		}
	};

	const TemplateComponent = TEMPLATES.find((t) => t.id === selectedTemplate)?.component || TEMPLATES[0].component;

	const templateProps = {
		companyName,
		jobTitle,
		jobDescription,
		qualification,
		jobLink,
		deadline,
		jobLocation,
	};

	return (
		<div className="p-6 flex flex-col items-center justify-center">
			{/* Template Selector */}
			<div className="flex gap-2 mb-4 justify-start items-center w-full">
				{TEMPLATES.map((template) => (
					<Button
						key={template.id}
						onClick={() => setSelectedTemplate(template.id)}
						variant={selectedTemplate === template.id ? "default" : "outline"}
						size="sm"
					>
						{template.name}
					</Button>
				))}
			</div>

			{/* Image Canvas */}
			<div className="shadow-lg" style={{ width: WIDTH / 2, height: HEIGHT / 2 }} ref={imageRef}>
				<TemplateComponent {...templateProps} />
			</div>

			{/* Action Buttons */}
			<div className="flex justify-center gap-4 mt-4">
				<button
					onClick={downloadImage}
					disabled={isDownloading || isCopying}
					className="flex items-center gap-2 bg-primary-800 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isDownloading ? <Spinner /> : <Download size={20} />}
					{isDownloading ? "Downloading..." : "Download Image"}
				</button>
				<button
					onClick={copyToClipboard}
					disabled={isDownloading || isCopying}
					className="flex items-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isCopying ? <Spinner /> : <Copy size={20} />}
					{isCopying ? "Copying..." : "Copy to Clipboard"}
				</button>
			</div>
		</div>
	);
}

export default GenerateSingleJobImage;

/* ─────────────────────────────────────────────
   Template 1: Classic (original centered card)
   ───────────────────────────────────────────── */

function TemplateClassic({ companyName, jobTitle, jobDescription, qualification, jobLink, deadline, jobLocation }) {
	return (
		<div className="bg-primary-800 p-12 h-full w-full">
			<div className="bg-white flex items-center h-full justify-center flex-col px-4">
				<img src="/images/logo.png" className="w-24 mb-2" />

				<h2 className="text-3xl font-plus-jakarta" contentEditable>
					{companyName}
				</h2>
				<h1 className="text-2xl font-plus-jakarta">is Hiring</h1>

				<div className="mt-4 border-2 w-10/12 border-primary-800 rounded-full py-3">
					<h3 className="text-xl text-center font-semibold px-6" contentEditable>
						{jobTitle}
					</h3>
				</div>

				<div className="w-10/12 mt-6">
					<p className="text-center text-sm leading-tight">{jobDescription}</p>
				</div>

				<div className="my-4 w-10/12 text-white bg-primary-800 rounded-full py-1 px-6">
					<p className="text-center font-semibold text-sm" contentEditable>
						{qualification}
					</p>
				</div>

				<div className="grid grid-cols-2 w-10/12 gap-4 mt-4">
					<div className="flex items-center gap-2">
						<div className="text-white bg-primary-800 rounded-full p-2">
							<CalendarDays size={12} />
						</div>
						<div className="font-medium text-sm leading-none">{deadline}</div>
					</div>
					<div className="flex items-center gap-2">
						<div className="text-white bg-primary-800 rounded-full p-2">
							<MapPin size={12} />
						</div>
						<div className="font-medium text-sm leading-none">{jobLocation}</div>
					</div>
					<div className="flex items-center gap-2 col-span-2">
						<div className="text-white bg-primary-800 rounded-full p-2">
							<Link size={12} />
						</div>
						<div className="font-medium text-sm leading-none">{jobLink}</div>
					</div>
				</div>
			</div>
		</div>
	);
}

/* ─────────────────────────────────────────────
   Template 2: Editorial (dark poster layout)
   ───────────────────────────────────────────── */

function TemplateEditorial({ companyName, jobTitle, jobDescription, qualification, jobLink, deadline, jobLocation }) {
	return (
		<div className="h-full w-full flex flex-col bg-blue-800">
			{/* Gold accent bar */}
			<div className="w-full" style={{ height: 6, backgroundColor: "#e8b84b" }} />

			<div className="flex flex-col flex-1 px-10 pt-8 pb-6">
				{/* Logo */}
				<img src="/media-kit/logos/logo-dark.svg" className="w-20 mb-8" />

				{/* Company + Hiring */}
				<div className="mb-2">
					<p
						className="text-lg font-plus-jakarta font-semibold uppercase tracking-widest"
						style={{ color: "#e8b84b" }}
						contentEditable
					>
						{companyName}
					</p>
					<h1
						className="font-plus-jakarta font-bold uppercase leading-none text-white"
						style={{ fontSize: 28, letterSpacing: "-0.02em" }}
					>
						Is Hiring
					</h1>
				</div>

				{/* Job title — white band, full bleed */}
				<div className="py-4 px-10 my-4 -mx-10 bg-white">
					<h2
						className="font-plus-jakarta font-bold uppercase tracking-wide text-blue-900"
						style={{ fontSize: 34, lineHeight: 1.2 }}
						contentEditable
					>
						{jobTitle}
					</h2>
				</div>

				{/* Description */}
				<p className="text-sm leading-relaxed mt-1 text-white">{jobDescription}</p>

				{/* Qualification pill */}
				<div className="mt-5 py-2 px-5 rounded-full w-fit" style={{ border: "2px solid #e8b84b" }}>
					<p className="text-sm font-semibold text-white" contentEditable>
						{qualification}
					</p>
				</div>

				{/* Spacer */}
				<div className="flex-1 min-h-4" />

				{/* Divider */}
				<div className="w-full h-px mb-5 bg-blue-600" />

				{/* Info items */}
				<div className="flex flex-col gap-3 mb-4">
					<div className="flex items-center gap-3">
						<div
							className="rounded-full flex items-center justify-center bg-blue-700"
							style={{ width: 28, height: 28 }}
						>
							<CalendarDays size={13} style={{ color: "#e8b84b" }} />
						</div>
						<span className="text-sm text-white font-medium">{deadline}</span>
					</div>
					<div className="flex items-center gap-3">
						<div
							className="rounded-full flex items-center justify-center bg-blue-700"
							style={{ width: 28, height: 28 }}
						>
							<MapPin size={13} style={{ color: "#e8b84b" }} />
						</div>
						<span className="text-sm text-white font-medium">{jobLocation}</span>
					</div>
					<div className="flex items-center gap-3">
						<div
							className="rounded-full flex items-center justify-center bg-blue-700"
							style={{ width: 28, height: 28 }}
						>
							<Link size={13} style={{ color: "#e8b84b" }} />
						</div>
						<span className="text-sm text-white font-medium">{jobLink}</span>
					</div>
				</div>

				{/* Footer */}
				<div className="pt-3 border-t border-blue-600">
					<p className="text-xs text-center tracking-widest uppercase text-blue-200">www.dproz.com</p>
				</div>
			</div>
		</div>
	);
}
